import React, { useState } from "react";
import { Loader2, Search, Map } from "lucide-react";
import { toast } from "sonner";
import { adminDiscoverComparators, adminBuildSegmentMap } from "@/lib/api";

/**
 * Operator tool: find comparator brands, approve them, then measure where
 * everyone sits on price.
 *
 * Two deliberately separate steps, and the separation is the safety property.
 *
 * STEP 1 asks a search to propose which brands are worth reading. It returns
 * names and domains and nothing else: no prices, no sizes, no "premium" labels.
 * Nothing from this step reaches a client.
 *
 * STEP 2 reads the approved stores' own catalogues and computes the price axis
 * from what they publish. Every figure a client ever sees comes from here.
 *
 * It lives in /admin rather than inside the audit because the audit is a paid
 * deliverable with a provenance record built for reconstructability, and a
 * search is not deterministic: two runs of the same audit could otherwise
 * disagree about who was in the comparison.
 */
export default function SegmentFinder({ subjectDomain = "" }) {
  const [subject, setSubject] = useState(subjectDomain);
  const [category, setCategory] = useState("");
  const [subtype, setSubtype] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [approved, setApproved] = useState({});
  const [map, setMap] = useState(null);
  const [busy, setBusy] = useState(null);

  const discover = async () => {
    setBusy("discover");
    try {
      const r = await adminDiscoverComparators({ category, subtype, country: "Australia" });
      setCandidates(r.candidates || []);
      const next = {};
      (r.candidates || []).forEach((c) => { if (!c.excluded) next[c.domain] = true; });
      setApproved(next);
      if (!(r.candidates || []).length) toast.message("No candidates returned. Add domains by hand.");
    } catch {
      toast.error("Discovery failed. You can still enter domains by hand below.");
    } finally { setBusy(null); }
  };

  const build = async () => {
    const domains = Object.entries(approved).filter(([, v]) => v).map(([d]) => d);
    if (!subject || domains.length < 1) {
      toast.error("Enter the subject store and approve at least one other.");
      return;
    }
    setBusy("map");
    try {
      setMap(await adminBuildSegmentMap({ subject_domain: subject, domains }));
    } catch {
      toast.error("Could not read those catalogues.");
    } finally { setBusy(null); }
  };

  const addByHand = (e) => {
    e.preventDefault();
    const v = (e.target.elements.dom.value || "").trim();
    if (!v) return;
    setApproved((a) => ({ ...a, [v]: true }));
    setCandidates((c) => (c.some((x) => x.domain === v) ? c : [...c, { brand: "Added by hand", domain: v }]));
    e.target.reset();
  };

  return (
    <section className="card-cream p-6 md:p-8" data-testid="segment-finder">
      <div className="tick text-[#9a3412] mb-1">Comparator finder</div>
      <h3 className="font-serif-display text-2xl text-[#171717]">Where a brand sits on price</h3>
      <p className="mt-2 font-mono-tech text-[12px] text-[#525252] leading-relaxed max-w-[70ch]">
        Search proposes which brands to read. It never returns a price or a label. Approve the
        list, and every figure below is then measured from each store's own published catalogue.
      </p>

      <div className="mt-6 grid md:grid-cols-3 gap-3">
        <input
          value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject store domain" data-testid="segment-subject"
          className="px-3 py-2 bg-white border border-[#ddd8ce] font-mono-tech text-[13px]"
        />
        <input
          value={category} onChange={(e) => setCategory(e.target.value)}
          placeholder="Category (e.g. Apparel)" data-testid="segment-category"
          className="px-3 py-2 bg-white border border-[#ddd8ce] font-mono-tech text-[13px]"
        />
        <input
          value={subtype} onChange={(e) => setSubtype(e.target.value)}
          placeholder="Subtype (e.g. Womenswear)" data-testid="segment-subtype"
          className="px-3 py-2 bg-white border border-[#ddd8ce] font-mono-tech text-[13px]"
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <button onClick={discover} disabled={busy === "discover"} data-testid="segment-discover"
          className="btn-secondary inline-flex items-center gap-2">
          {busy === "discover" ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Propose brands to read
        </button>
        <button onClick={build} disabled={busy === "map"} data-testid="segment-build"
          className="btn-primary inline-flex items-center gap-2">
          {busy === "map" ? <Loader2 size={14} className="animate-spin" /> : <Map size={14} />}
          Read approved stores and place them
        </button>
      </div>

      {!!candidates.length && (
        <div className="mt-6">
          <div className="tick mb-2">Approve who gets read ({Object.values(approved).filter(Boolean).length})</div>
          <div className="grid md:grid-cols-2 gap-x-8">
            {candidates.map((c) => (
              <label key={c.domain}
                className={`flex items-center gap-3 py-1.5 font-mono-tech text-[12.5px] ${c.excluded ? "opacity-45" : ""}`}>
                <input type="checkbox" disabled={c.excluded}
                  checked={!!approved[c.domain]}
                  onChange={(e) => setApproved((a) => ({ ...a, [c.domain]: e.target.checked }))}
                />
                <span className="text-[#171717]">{c.brand || c.domain}</span>
                <span className="text-[#525252]">{c.domain}</span>
                {c.excluded && <span className="text-[#9a3412]">excluded on request</span>}
              </label>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={addByHand} className="mt-4 flex gap-2">
        <input name="dom" placeholder="Add a domain by hand" data-testid="segment-add-domain"
          className="px-3 py-2 bg-white border border-[#ddd8ce] font-mono-tech text-[12.5px] flex-1 max-w-sm" />
        <button type="submit" className="btn-secondary">Add</button>
      </form>

      {map && (
        <div className="mt-8 border-t border-[#ddd8ce] pt-6" data-testid="segment-map-result">
          {map.subject?.read && (
            <p className="font-mono-tech text-[13px] text-[#171717] leading-relaxed mb-4">{map.subject.read}</p>
          )}
          {map.segments_unavailable && (
            <p className="font-mono-tech text-[12px] text-[#9a3412] leading-relaxed mb-4">{map.segments_unavailable}</p>
          )}
          <div className="divide-y divide-[#ddd8ce]">
            {(map.stores || []).map((s) => (
              <div key={s.domain} className="py-2 grid grid-cols-[1fr_auto_auto] gap-4 items-center">
                <span className={`font-mono-tech text-[13px] ${s.source === "subject" ? "text-[#9a3412] font-medium" : "text-[#171717]"}`}>
                  {s.domain}{s.source === "subject" ? " (subject)" : ""}
                </span>
                <span className="font-mono-tech text-[12px] text-[#404040] tabular-nums">
                  {map.currency} {Number(s.median_price).toLocaleString()}
                </span>
                <span className="tick text-[#525252]">{s.segment_label || ""}</span>
              </div>
            ))}
          </div>
          {map.basis && (
            <p className="mt-4 font-mono-tech text-[11px] text-[#525252] leading-relaxed">{map.basis}</p>
          )}
          {!!(map.unreadable || []).length && (
            <p className="mt-2 font-mono-tech text-[11px] text-[#525252]">
              Could not read: {map.unreadable.join(", ")}. Reported rather than dropped.
            </p>
          )}
          {(map.excluded || []).map((e) => (
            <p key={e.domain} className="mt-2 font-mono-tech text-[11px] text-[#525252]">
              {e.domain} was read but left off the axis. {e.reason}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}
